import { cloneDeep, concat, set } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components';
import FormDrawer from '~/components/FormDrawer';
import { useGetApplication } from '~/controllers/wizard';
import { PlusIcon } from '~/img';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import buildNamePath from '~/utils/buildNamePath';
import { hasSoftDeclineFields } from '~/utils/softDeclinesFields';
import { useContactAdd } from '~/utils/useContactAdd';

import { shareholdersKey } from '.';
import { MatchingContactsModal } from '../MatchingContactsModal';
import { Individual, IndividualAddProps } from './IndividualAdd';
import IndividualForm, { formName } from './IndividualForm';
import { isShareholderEditButtonDisabled } from './utils';

const BeneficialOwnerAdd: React.FC<IndividualAddProps> = (props) => {
  const { value, onChange, setSourceOfEditing } = props;
  const shareholdersFormState = { shareholders: value };

  const { t } = useTranslation('fillApplication');
  const applicationQuery = useGetApplication();
  const hasBeneficialOwnersValidationWarnings = hasSoftDeclineFields(
    applicationQuery.data,
    'shareholders.beneficialOwners[]',
  );
  const newShareholderIndex = value?.beneficialOwners?.length ?? 0;

  const {
    drawerOpen,
    setDrawerOpen,
    modalOpen,
    onCancelModal,
    onCloseDrawer,
    contactState,
    setSelectedContact,
    handleCreateNewContact,
    handleSelectContact,
    onFinish,
  } = useContactAdd({
    sourceOfEditingValue: 'shareholder',
    setSourceOfEditing,
    onUpdate: (contact: Individual) => {
      const deepCloneValue = cloneDeep(value);
      set(deepCloneValue!, ['beneficialOwners', `${newShareholderIndex}`], contact);
      onChange?.(deepCloneValue!);
    },
    getNewContact: (newValues: ApplicationPayload) =>
      newValues.shareholders?.beneficialOwners?.[newShareholderIndex] as Individual,
    getNewContactData: (newContact: Individual) => newContact.data,
    setContactIds: (newContact: Individual, contactId: string) => {
      set(newContact, ['data', 'matchContactId'], contactId);
      set(newContact, ['data', 'contactId'], contactId);
    },
    setNewContactUserRoles: (newContact: Individual, userRoles: Array<string>) => {
      set(newContact, ['data', 'userRoles'], concat(newContact.data.userRoles, userRoles));
    },
  });

  return (
    <>
      <Button
        id="addNewBeneficialOwnerButton"
        extraLarge
        type="default"
        icon={<PlusIcon />}
        disabled={isShareholderEditButtonDisabled(applicationQuery.data, hasBeneficialOwnersValidationWarnings)}
        onClick={() => setDrawerOpen(true)}
      >
        {t('Add beneficial owner')}
      </Button>
      <FormDrawer
        id="beneficialOwnerAddDrawer"
        open={drawerOpen}
        onClose={onCloseDrawer}
        title={t('Add beneficial owner')}
        formName={formName}
      >
        <MatchingContactsModal
          open={modalOpen}
          title={t(
            'This contact matches existing contacts. If you wish to update any of the contacts below, please select the contact you want to update, or create a new contact',
          )}
          listData={contactState.matchingContactList}
          selectedContact={contactState.selectedContact}
          onCancel={onCancelModal}
          setSelectedContact={setSelectedContact}
          handleCreateNewContact={handleCreateNewContact}
          handleSelectContact={handleSelectContact}
        />
        <IndividualForm
          onFinish={onFinish}
          initialValues={shareholdersFormState}
          n={buildNamePath([shareholdersKey, 'beneficialOwners', newShareholderIndex])}
          shareholderType="BENEFICIAL_OWNER"
        />
      </FormDrawer>
    </>
  );
};

export default BeneficialOwnerAdd;
